/**
 * C2 (round 8) -- one sentence, one renderer.
 *
 * "N of M steps complete" is the first line an administrator reads on
 * the home ribbon, and it is also on Getting Started and on the Journey
 * spine. Three renderers, and the round found them disagreeing about
 * whether the figures were isolated -- which is a defect whether or not
 * the disagreement is visible, because the next person to change one of
 * them has no way to know the other two exist.
 *
 * MEASURED BEFORE CHANGING ANYTHING, and the measurement did not match
 * the report: at 1440x900 in the pseudo-locale the teaser and the
 * Journey both compute `unicode-bidi: isolate` and Getting Started
 * computes `normal` -- one of the two named as unguarded was already
 * guarded. All three render their figures in the right order, because
 * a BARE number cannot reorder against neighbouring text; that is the
 * round 7 finding that made "10 of 14" correct again after isolating it
 * had made it wrong, and it still holds.
 *
 * So this does not reintroduce an isolate. It removes the thing that
 * actually was wrong: three copies of one sentence, able to drift.
 *
 * ROUND 9 D1 -- the round read the resulting `unicode-bidi: normal` as
 * a removed guard. Settled by measuring where the figures are actually
 * PAINTED, rather than by either side asserting: in the pseudo-locale
 * all three render the first figure RIGHTMOST, which is correct for an
 * RTL line.
 *
 *   journey-progress     20 @x1075   23 @x1011
 *   onboarding-progress   6 @x1304    7 @x1246
 *   teaser-progress       7 @x824     7 @x766
 *
 * The style property and the painted outcome are different questions.
 * Re-run with tools/verify-drives/d1-figure-order.mjs, which also
 * records what could not be constructed and therefore what this does
 * not prove.
 */
import { useTranslation } from "react-i18next";

export function StepProgress({
  done,
  total,
  testid,
  className,
}: {
  done: number;
  total: number;
  testid?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <span data-testid={testid} className={className}>
      {t("onboarding.progress", { completed: done, total })}
    </span>
  );
}
